/**
 * Cloud/web-service adapter for follow-up indexing work.
 *
 * Customization saves return before their data is reflected by search. The
 * Regular settings keep the historical fire-and-forget rebuild contract.
 * Merchandising publications additionally track the authoritative config/live
 * revision pair, without inventing percentage progress the cloud API does not
 * expose.
 */
import {
  notifyIndexingRequested,
  notifyIndexingRequestFailed,
} from 'features/operation-supervisor/indexingRequestEvents';

const CANONICAL_JOB_PREFIX = 'cloud-full';

const normalizedStatus = (value) => {
  const status = String(value || '').trim().toLowerCase();
  if (['success', 'completed', 'complete', 'done'].includes(status)) return 'completed';
  if (['running', 'in_progress', 'processing'].includes(status)) return 'running';
  if (['scheduled', 'queued', 'pending'].includes(status)) return 'queued';
  if (['error', 'failed', 'failure'].includes(status)) return 'failed';
  return status || 'idle';
};

const revisionAtLeast = (published, target) => {
  if (published === null || published === undefined) return false;
  const left = String(published);
  const right = String(target);
  if (left === right) return true;
  if (!/^\d+$/.test(left) || !/^\d+$/.test(right)) return false;
  const normalizedLeft = left.replace(/^0+(?=\d)/, '');
  const normalizedRight = right.replace(/^0+(?=\d)/, '');
  return normalizedLeft.length > normalizedRight.length
    || (normalizedLeft.length === normalizedRight.length && normalizedLeft > normalizedRight);
};

const canonicalJobId = (shopId, revision) => (
  `${CANONICAL_JOB_PREFIX}:${encodeURIComponent(shopId)}:${encodeURIComponent(revision)}`
);

const parseCanonicalJobId = (jobId) => {
  const match = /^cloud-full:([^:]+):([^:]+)$/.exec(String(jobId));
  if (!match) throw new Error('Unknown cloud indexing job');
  try {
    return { shopId: decodeURIComponent(match[1]), targetRevision: decodeURIComponent(match[2]) };
  } catch (error) {
    throw new Error('Invalid cloud indexing job');
  }
};

const normalizeHistoryJob = (job) => {
  const status = normalizedStatus(job?.status);
  const indexedCount = job?.indexed_count ?? job?.indexedCount ?? job?.stats?.itemsProcessed ?? null;
  const totalCount = job?.total_count ?? job?.totalCount
    ?? job?.stats?.itemsTotal ?? job?.stats?.totalItems ?? null;
  return {
    job_id: String(job?.job_id ?? job?.jobId ?? job?.taskId ?? job?._id),
    status,
    progress: job?.progress ?? job?.stats?.progress ?? (status === 'completed' ? 100 : null),
    indexed_count: indexedCount,
    total_count: totalCount,
    created_at: job?.created_at ?? job?.createdAt ?? null,
    started_at: job?.started_at ?? job?.startedAt ?? null,
    finished_at: job?.finished_at ?? job?.finishedAt ?? null,
    error: job?.error ?? (status === 'failed' ? job?.message : null) ?? null,
  };
};

export function createLegacyIndexingApi({ client, shopId: configuredShopId } = {}) {
  let resolvedClient = client || null;

  function getClient() {
    if (!resolvedClient) {
      // eslint-disable-next-line global-require
      const { wsClient } = require('util/appUtils');
      resolvedClient = wsClient;
    }
    return resolvedClient;
  }

  function getShopId(explicitShopId = null) {
    if (explicitShopId || configuredShopId) return explicitShopId || configuredShopId;
    // eslint-disable-next-line global-require
    return require('util/appUtils').getShopIdFromLocation();
  }

  async function readIndexState(shopId) {
    const response = await getClient().prepareRequest(
      'POST',
      `/system/shop/${encodeURIComponent(shopId)}/merchandising/actions/read`,
      { body: { action: 'merchandising.index_state.get', payload: {} } },
    );
    if (response?.result === 'error') {
      throw new Error(response.error || response.message || 'Failed to load index publication state');
    }
    if (response?.result !== 'success' || !response.data || typeof response.data !== 'object') {
      throw new Error('The cloud index publication state is invalid');
    }
    return response.data;
  }

  async function startFull(shopId) {
    if (!shopId) throw new Error('shopId is required to start indexing');
    notifyIndexingRequested({ source: 'cloud-indexing-api', shopId });
    try {
      const response = await getClient().prepareRequest(
        'POST',
        `/system/shop/${encodeURIComponent(shopId)}/index/rebuild`,
        { qs: { background: true, full: true } },
      );
      if (response?.status && response.status !== 'ok') {
        throw new Error(response.error || 'Failed to start indexing');
      }
      return {
        status: 'started',
        operation: 'indexing',
        background: response?.background !== false,
      };
    } catch (error) {
      notifyIndexingRequestFailed({
        source: 'cloud-indexing-api',
        shopId,
        error: error?.message || 'Failed to start indexing',
      });
      throw error;
    }
  }

  return {
    startFull,

    async startCanonicalFull(shopId = null) {
      const resolvedShopId = getShopId(shopId);
      if (!resolvedShopId) throw new Error('shopId is required to start indexing');
      const before = await readIndexState(resolvedShopId);
      const targetRevision = String(
        before.index_required_revision ?? before.config_revision ?? '',
      );
      if (!targetRevision) throw new Error('The pending index revision is missing');
      const jobId = canonicalJobId(resolvedShopId, targetRevision);
      const alreadyPublished = revisionAtLeast(before.live_index_revision, targetRevision);
      if (!alreadyPublished) await startFull(resolvedShopId);
      return {
        jobId,
        indexing: {
          state: alreadyPublished ? 'completed' : 'queued',
          progress: alreadyPublished ? 100 : null,
          target_revision: targetRevision,
          live_revision: before.live_index_revision ?? null,
        },
      };
    },

    async getStatus(jobId) {
      const { shopId, targetRevision } = parseCanonicalJobId(jobId);
      const state = await readIndexState(shopId);
      const publishedRevision = state.live_index_revision ?? state.live_revision ?? null;
      const published = revisionAtLeast(publishedRevision, targetRevision);
      return {
        jobId: String(jobId),
        state: published ? 'completed' : (state.dirty === false ? 'failed' : 'running'),
        progress: published ? 100 : null,
        error: !published && state.dirty === false
          ? 'The index completed without publishing the requested revision'
          : null,
        targetRevision,
        publishedRevision,
        indexing: state,
      };
    },

    async getHistory(limit = 10) {
      const shopId = getShopId();
      if (!shopId) throw new Error('shopId is required to load indexing history');
      const safeLimit = Math.max(1, Math.min(30, Number(limit) || 10));
      const response = await getClient().prepareRequest(
        'GET',
        `/system/shop/${encodeURIComponent(shopId)}/task/crawler/history`,
        { qs: { limit: safeLimit } },
      );
      const history = Array.isArray(response?.history) ? response.history : [];
      return { history: history.slice(0, safeLimit).map(normalizeHistoryJob) };
    },
  };
}
